
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useStatsConfirmation } from '@/hooks/usePlayerStats';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Clock } from 'lucide-react';

const StatsConfirmationPanel = () => {
  const [selectedGameId, setSelectedGameId] = useState<number>(0);
  const [games, setGames] = useState<any[]>([]);
  
  const { unconfirmedStats, loading, confirmStat } = useStatsConfirmation(selectedGameId || undefined);

  useEffect(() => {
    fetchGames();
  }, []);

  const fetchGames = async () => {
    try {
      const { data, error } = await supabase
        .from('events' as any)
        .select('event_id, event_name, event_date')
        .order('event_date', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching games:', error);
        return;
      }

      setGames(data || []);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const handleConfirmStat = async (statId: number) => {
    const success = await confirmStat(statId);
    if (success) {
      // Stats will be refreshed automatically via the hook
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Stats Confirmation Panel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Select 
              value={selectedGameId.toString()} 
              onValueChange={(value) => setSelectedGameId(parseInt(value))}
            >
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select a game to review stats" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {games.map((game) => (
                  <SelectItem key={game.event_id} value={game.event_id.toString()}>
                    {game.event_name} - {new Date(game.event_date).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedGameId > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-yellow-500" />
                <h3 className="text-lg font-semibold text-white">
                  Pending Stats Confirmation ({unconfirmedStats.length})
                </h3>
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse bg-gray-700 rounded-lg h-20"></div>
                  ))}
                </div>
              ) : unconfirmedStats.length > 0 ? (
                <div className="space-y-3">
                  {unconfirmedStats.map((stat) => (
                    <div
                      key={stat.stat_id}
                      className="flex items-center justify-between p-4 bg-gray-700 rounded-lg border border-gray-600"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-medium text-white">
                            Player: {stat.player_id.substring(0, 8)}...
                          </p>
                          <Badge variant="secondary" className="bg-yellow-600 text-white">
                            Pending
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Stat Type:</span>
                            <span className="text-white ml-2 capitalize">
                              {stat.stat_type.replace('_', ' ')}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-400">Value:</span>
                            <span className="text-white ml-2">{stat.stat_value}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Sport ID:</span>
                            <span className="text-white ml-2">{stat.sport_id}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Submitted:</span>
                            <span className="text-white ml-2">
                              {new Date(stat.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        <Button
                          onClick={() => handleConfirmStat(stat.stat_id)}
                          className="bg-sports-green text-black hover:bg-green-600"
                          size="sm"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Confirm
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-400 py-8">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-sports-green" />
                  <p>All stats for this game have been confirmed!</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StatsConfirmationPanel;
